import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SidebarLinkGroup from "./SidebarLinkGroup";
import { useAuth } from "src/contexts/auth-provider";
import { ownerSidebar, restaurantSidebar } from "./sidebarRoutes";
import HelpIcon from "../../assests/images/dashboard/help.png";
import SidebarItem from "./SidebarItem";
import {
  FRONTEND_URL,
  GRAPHQL_ENDPOINT,
  S3_BASE_URL,
} from "src/constants/endpoints";
import RestuarantImg from "../../assests/images/dashboard/test.png";
import { useDispatch } from "react-redux";
import useOutsideClick from "src/hooks/useOutsideClick";
import {
  set_menu_categories,
  set_menu_category_loading,
} from "src/store/features/category-slice";
import { GET_MENU_CATEGORIES } from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { roleBasesRoute } from "src/utils/roleBasesRoute";
import { SidebarRoutesEnum } from "src/enums/sidebarRoutes.enum";
interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
  setPageTitle: any;
  hideTopRow?: boolean;
  /** Narrow viewport: drawer overlay, aligned with Tailwind max-md */
  isMobileNav?: boolean;
}

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  setPageTitle,
  hideTopRow = false,
  isMobileNav = false,
}: SidebarProps) => {
  const [sidebarRoutes, setSidebarRoutes]: any = useState([]);
  const [isCategoryLoader, setIsCategoryLoader]: any = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    hasRestaurant,
    sethasRestaurant,
    permissions,
  } = useAuth();
  const { pathname } = location;
  const { axBe } = useAxiosInterceptor();

  const getCategories = useCallback(async () => {
    dispatch(set_menu_category_loading(true));
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_MENU_CATEGORIES,
        variables: {
          storeCorrelationId: hasRestaurant?.correlationId,
          activeFilter: "ACTIVE",
        },
      });
      dispatch(
        set_menu_categories({
          categories: data?.data?.categoriesByStoreCorrelationId || [],
        })
      );
      setTimeout(() => {
        setIsCategoryLoader(false);
        dispatch(set_menu_category_loading(false));
      }, 500);
    } catch (error) {
      console.error(error);
    }
  }, [axBe, dispatch, hasRestaurant?.correlationId]);

  useEffect(() => {
    if (!hasRestaurant) {
      setSidebarRoutes(ownerSidebar);
    } else {
      let routes = roleBasesRoute(restaurantSidebar, permissions);
      // Hide owner-only report links from managers (e.g. Tax)
      if (hasRestaurant?.storeStaffRole !== "OWNER") {
        routes = routes.map((r: any) => {
          if (r?.name === "Reports" && Array.isArray(r?.children)) {
            return {
              ...r,
              children: r.children.filter((c: any) => c?.name !== "Tax"),
            };
          }
          return r;
        });
      }
      setSidebarRoutes(routes);
    }
    if (hasRestaurant && !isCategoryLoader) {
      setIsCategoryLoader(true);
      getCategories();
    }
  }, [hasRestaurant, permissions]);

  useEffect(() => {
    const path = window.location.pathname;
    setSidebarRoutes((prev: any[]) =>
      prev.map((r: any) => {
        if (r?.name === "Reports" && path.includes("/reports"))
          return { ...r, isSubMenuOpen: true };
        if (r?.name === "Finance" && path.includes("/finance"))
          return { ...r, isSubMenuOpen: true };
        if (r?.name === "Menu" && (path.includes("/menu") || path.includes("/modifier") || path.includes("/combo")))
          return { ...r, isSubMenuOpen: true };
        return r;
      })
    );
  }, [pathname]);

  const toggleMenu = (parentIndex: number, childIndex?: number) => {
    setSidebarRoutes((prev: any[]) => {
      const next = [...prev];
      if (childIndex === undefined) {
        const route = next[parentIndex];
        if (route) {
          next[parentIndex] = { ...route, isSubMenuOpen: !route.isSubMenuOpen };
        }
      } else {
        const parent = next[parentIndex];
        if (parent?.children?.[childIndex]) {
          const child = parent.children[childIndex];
          const newChildren = [...parent.children];
          newChildren[childIndex] = { ...child, isSubMenuOpen: !child.isSubMenuOpen };
          next[parentIndex] = { ...parent, children: newChildren };
        }
      }
      return next;
    });
  };

  const changeModeToAdmin = () => {
    localStorage.removeItem("restaurant");
    localStorage.removeItem("employee");
    sethasRestaurant(null);
    window.location.href = FRONTEND_URL + "/my-restaurants";
  };

  const [restaurantDropdownOpen, setRestaurantDropdownOpen] = useState(false);
  const restaurantDropdownRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: restaurantDropdownRef, handler: () => setRestaurantDropdownOpen(false) });

  const addressLine =
    hasRestaurant?.address?.addressLine1?.trim() ?? "";
  const addressTruncated =
    addressLine.length > 28 ? addressLine.slice(0, 25) + "…" : addressLine;

  const asideClassName = [
    "flex flex-col overflow-y-hidden bg-[#EBEEF2] duration-300 ease-linear dark:bg-slate-900",
    isMobileNav
      ? "fixed left-0 top-14 z-[40] h-[calc(100vh-3.5rem)] w-[240px] shadow-[2px_0_20px_rgba(0,0,0,0.08)] transition-transform ease-linear"
      : "sticky top-14 z-10 h-[calc(100vh-56px)] shrink-0 shadow-[2px_0_20px_rgba(0,0,0,0.04)]",
    isMobileNav && !sidebarOpen && "-translate-x-full pointer-events-none",
    isMobileNav && sidebarOpen && "translate-x-0",
    !isMobileNav && (sidebarOpen ? "w-[240px]" : "w-[72px]"),
    !isMobileNav && "shrink-0",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className={asideClassName}>
      {hasRestaurant && (
        <div className="relative shrink-0 p-3" ref={restaurantDropdownRef}>
          <button
            type="button"
            className="flex h-11 w-full items-center justify-between gap-2 rounded-[12px] border-0 bg-black/[0.03] py-2 px-3 text-left font-medium transition-colors hover:bg-black/[0.06] dark:bg-white/5 dark:hover:bg-white/10"
            onClick={() => setRestaurantDropdownOpen(!restaurantDropdownOpen)}
          >
            {sidebarOpen ? (
              <>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {hasRestaurant?.name}
                  </div>
                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {addressTruncated || "—"}
                  </div>
                </div>
                <svg
                  className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${restaurantDropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </>
            ) : (
              <div className="flex w-full justify-center">
                <img
                  className="h-8 w-8 rounded-lg object-cover"
                  src={
                    hasRestaurant?.bucketKeyName
                      ? hasRestaurant?.bucketKeyName.includes("blob")
                        ? hasRestaurant?.bucketKeyName
                        : S3_BASE_URL + hasRestaurant?.bucketKeyName
                      : RestuarantImg
                  }
                  alt=""
                />
              </div>
            )}
          </button>
          {restaurantDropdownOpen && (
            <div
              className={`absolute mt-1 rounded-xl border border-black/[0.06] bg-white py-1 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-slate-800 ${
                sidebarOpen ? "left-3 right-3" : "left-2 right-2"
              }`}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                onClick={changeModeToAdmin}
              >
                Switch restaurant
              </button>
            </div>
          )}
        </div>
      )}

      <div className="no-scrollbar flex flex-1 flex-col overflow-y-auto px-2.5 pb-4">
        <nav className="mt-3">
          <ul className="mb-20 flex flex-col gap-0.5">
            <SidebarLinkGroup
              activeCondition={
                pathname === "/" || pathname === "/my-restaurants"
              }
            >
              {(_handleClick: () => void) => (
                <React.Fragment>
                  <ul className="flex flex-col gap-1">
                    {sidebarRoutes?.map((route: any, index: number) => (
                      <div key={index} className="mb-1 last:mb-0">
                        {!route?.children?.length ? (
                          <SidebarItem
                            index={index}
                            route={route}
                            isSubRoute={false}
                            sidebarOpen={sidebarOpen}
                            setPageTitle={setPageTitle}
                          />
                        ) : null}
                        {route?.children?.length ? (
                          <>
                            <SidebarItem
                              index={index}
                              route={route}
                              isSubRoute={false}
                              sidebarOpen={sidebarOpen}
                              hasDropdown={!!route?.children?.length}
                              toggleMenu={(i: number) => toggleMenu(i)}
                              setPageTitle={setPageTitle}
                              isSubMenuOpen={route?.isSubMenuOpen}
                            />
                            {route?.isSubMenuOpen &&
                              sidebarOpen &&
                              route?.children?.map(
                                (subRoute: any, subIndex: number) => (
                                  <SidebarItem
                                    key={`${index}-${subIndex}`}
                                    index={subIndex}
                                    parentIndex={index}
                                    route={subRoute}
                                    isSubRoute={true}
                                    sidebarOpen={sidebarOpen}
                                    setPageTitle={setPageTitle}
                                  />
                                )
                              )}
                          </>
                        ) : (
                          <></>
                        )}
                      </div>
                    ))}
                  </ul>
                </React.Fragment>
              )}
            </SidebarLinkGroup>
          </ul>
          <div className={`${!hasRestaurant && "fixed bottom-6"} py-3`}>
            <div
              className="flex min-h-9 max-md:min-h-11 md:h-9 min-w-0 items-center gap-2.5 rounded-[8px] px-3 py-2 max-md:py-2.5 md:py-1.5 pb-4"
              aria-label="Help Center"
            >
              <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center">
                <img className="h-4 w-4 opacity-75" src={HelpIcon} alt="" aria-hidden />
              </span>
              {sidebarOpen && (
                <span className="min-w-0 flex-1 truncate text-left text-xs font-medium text-[#475569] dark:text-slate-400">
                  Help Center
                </span>
              )}
            </div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
